import { Component, Input, Output, EventEmitter } from '@angular/core'
import { format, max, min, scaleQuantile, scaleTime } from 'd3'
import {
  toDate,
  addDays,
  isSameDay,
  isoWeekday,
  formatDate,
} from 'app/utils/date'
import { Colors } from '../../config/constants'

@Component({
  standalone: false,
  selector: 'app-heat-map',
  templateUrl: './heat-map.component.html',
  styleUrls: ['./heat-map.component.scss'],
})
export class HeatMapComponent {
  _data = []
  defualtConfig = {
    colors: [Colors.colorAccent],
  }
  _config: any = { ...this.defualtConfig }
  colorScale = null
  formatter = format('.3s')
  tooltip = {
    hidden: true,
    name: '',
    interval: '',
    value: 0,
    x: 0,
  }
  xScale = null
  xLabels = []
  fullMonthNames = []
  _categories = []

  @Input() interactivity = true
  @Input() unit: string

  @Input()
  get config() {
    return this._config
  }

  set config(newConfig) {
    newConfig = newConfig || {}
    this._config = { ...this.defualtConfig, ...newConfig }
  }

  @Input()
  get data() {
    return this._data
  }

  set data(newData: any[]) {
    if (!newData || !newData.length) {
      return
    }
    this.makeXLabels()
    this._data = this.prepareData(newData)
  }

  get categories() {
    return this._categories
  }

  set categories(data) {
    this._categories = data[0] ? data[0].values : []
  }

  @Output() categoryClick = new EventEmitter()

  prepareData(newData: any[]) {
    const maxVal = max(newData, (d) => max(d.values, (d2) => d2.value))
    const minVal = min(newData, (d) => min(d.values, (d2) => d2.value))
    const colorScale = scaleQuantile()
      .range(this._config.colors)
      .domain([minVal, maxVal])
    this.colorScale = colorScale
    newData = newData.filter((d) =>
      this.xLabels.some((l) => isSameDay(toDate(l.value), toDate(d.start))),
    )

    this.categories = newData
    const data = newData
      .slice()
      .reverse()
      .map((elem) => {
        elem.name = this.config.deliveryNames[elem.delivery_id]
        return elem
      })
    let dataIndex = 0
    const filledData = this.xLabels.map((date) => {
      const currentColumn = data[dataIndex]
      if (!currentColumn) {
        return { empty: true }
      }
      let currentDate = toDate(currentColumn.start)
      if (isoWeekday(currentDate) === 7) {
        currentDate = addDays(currentDate, 1)
        date.value = addDays(toDate(date.value), 1)
      }
      const dateMatch = isSameDay(toDate(date.value), currentDate)
      if (dateMatch) {
        dataIndex++
      }
      return dateMatch ? currentColumn : { empty: true }
    })
    return filledData
  }

  makeXLabels() {
    const granularity = this.config.getCurrentGranularity()
    const dateLimits = this.config.getDateLimits()
    const tickInterval = this.config.getTickInterval()
    this.xScale = this.xScale || scaleTime().domain(dateLimits)
    this.xLabels = this.xScale.ticks(tickInterval).map((tick) => {
      const date = granularity === 'week' ? addDays(tick, 1) : new Date(tick)
      let opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' }
      if (date.getDate() === 1) {
        opts =
          date.getMonth() === 0
            ? { month: 'short', year: 'numeric' }
            : { month: 'short' }
      }
      const label = formatDate(date, opts)
      return {
        label: label.charAt(0).toUpperCase() + label.slice(1).replace('.', ''),
        value: date,
      }
    })
  }

  // from here: https://stackoverflow.com/questions/11867545/change-text-color-based-on-brightness-of-the-covered-background-area
  getLightness(hexColor) {
    const [r, g, b] = [1, 3, 5].map((p) => parseInt(hexColor.substr(p, 2), 16))
    return (r * 299 + g * 587 + b * 114) / 1000
  }

  handleClick(delivery, item) {
    const data = {
      category: item.interval,
      delivery_id: delivery.delivery_id,
    }
    this.categoryClick.emit(data)
  }

  tooltipEnter(delivery, item, event) {
    this.tooltip.x = event.layerX
    this.tooltip.name = delivery.name
    this.tooltip.interval = item.interval
    this.tooltip.value = format(',.0f')(item.value)
    this.tooltip.hidden = false
  }

  tooltipExit() {
    this.tooltip.hidden = true
  }
}
